import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PropTypes from 'prop-types';
import './Alert.css';

/**
 * Alert Component
 * Information banners with close functionality
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Alert content
 * @param {string} props.variant - Visual variant: 'success', 'error', 'warning', 'info'
 * @param {string} props.title - Alert title
 * @param {boolean} props.closeable - Show close button
 * @param {Function} props.onClose - Close handler
 * @param {boolean} props.icon - Show icon
 * @param {string} props.className - Additional CSS classes
 */
const Alert = ({
  children,
  variant = 'info',
  title,
  closeable = false,
  onClose,
  icon = true,
  className = '',
}) => {
  const [isVisible, setIsVisible] = useState(true);

  const handleClose = () => {
    setIsVisible(false);
    if (onClose) {
      setTimeout(() => onClose(), 300);
    }
  };

  const getIcon = () => {
    if (!icon) return null;

    switch (variant) {
      case 'success':
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M16.6667 5L7.50001 14.1667L3.33334 10"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      case 'error':
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M10 6V10M10 14H10.01M18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      case 'warning':
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M10 6V10M10 14H10.01M8.28 2.28L1.82 13C1.56 13.45 1.42 13.96 1.42 14.48C1.42 15.79 2.48 16.85 3.79 16.85H16.21C17.52 16.85 18.58 15.79 18.58 14.48C18.58 13.96 18.44 13.45 18.18 13L11.72 2.28C11.2 1.41 10.13 1 9 1C7.87 1 6.8 1.41 6.28 2.28Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      case 'info':
      default:
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M10 14V10M10 6H10.01M18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
    }
  };

  const alertClasses = `
    alert
    alert--${variant}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className={alertClasses}
          role="alert"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, height: 0, marginBottom: 0 }}
          transition={{ duration: 0.3 }}
        >
          {icon && <div className="alert-icon">{getIcon()}</div>}

          <div className="alert-content">
            {title && <div className="alert-title">{title}</div>}
            <div className="alert-message">{children}</div>
          </div>

          {closeable && (
            <button
              className="alert-close"
              onClick={handleClose}
              aria-label="Close alert"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M12 4L4 12M4 4L12 12"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

Alert.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['success', 'error', 'warning', 'info']),
  title: PropTypes.string,
  closeable: PropTypes.bool,
  onClose: PropTypes.func,
  icon: PropTypes.bool,
  className: PropTypes.string,
};

export default Alert;
